"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import { Menu } from "lucide-react";
import ButtonLink from "@/components/site/button-link";
import DesktopNavigation from "@/components/layout/DesktopNavigation";
import DesktopNavigationFallback from "@/components/layout/DesktopNavigationFallback";
import Logo from "@/components/ui/Logo";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";
import { LOGIN_URL } from "@/lib/constants";
import { useRegisterUrl } from "@/lib/hooks/use-register-url";
import { useNavigationContent } from "@/lib/i18n/content-context";
import { useLocale } from "@/lib/i18n/locale-context";
import { useMediaQuery } from "@/lib/hooks/use-media-query";

const MobileMenuSheet = dynamic(
  () => import("@/components/layout/MobileMenuSheet"),
  { ssr: false },
);

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 880px)");
  const locale = useLocale();
  const {
    mainNavigation,
    pricingNavLink,
    headerUi: navOverrides,
  } = useNavigationContent();

  const registerUrl = useRegisterUrl();
  const navProps = {
    locale,
    mainNavigation,
    pricingNavLink,
  };

  return (
    <header className="site-header fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-3 sm:px-4 md:px-6 md:pt-4">
      <div className="relative w-full max-w-full min-[880px]:w-fit min-[880px]:max-w-[calc(100%-1rem)]">
        <div aria-hidden className="liquid-nav-glass site-glass" />
        <div className="relative z-10 flex h-14 pointer-events-auto items-center gap-2 ps-5 pe-3 md:ps-6 md:pe-4">
          <div className="flex h-full shrink-0 items-center">
            <Logo priority />
          </div>

          <div className="hidden flex-none min-[880px]:mx-6 min-[880px]:flex lg:mx-10 xl:mx-12 2xl:mx-14">
            {isDesktop ? (
              <DesktopNavigation {...navProps} />
            ) : (
              <DesktopNavigationFallback {...navProps} />
            )}
          </div>

          <div className="ms-auto flex shrink-0 items-center gap-1.5 sm:gap-2 md:gap-2.5">
            <ButtonLink
              href={LOGIN_URL}
              external
              variant="ghost"
              size="sm"
              className="hidden min-[360px]:inline-flex"
            >
              {navOverrides.loginLabel}
            </ButtonLink>
            <ButtonLink
              href={registerUrl}
              external
              size="sm"
              className="hidden min-[360px]:inline-flex"
            >
              {navOverrides.startTrialLabel}
            </ButtonLink>
          </div>

          <button
            type="button"
            className={cn(
              buttonVariants({ variant: "outline", size: "icon" }),
              "shrink-0 min-[880px]:hidden",
            )}
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu />
          </button>

          {mobileOpen ? (
            <MobileMenuSheet
              open={mobileOpen}
              onOpenChange={setMobileOpen}
              locale={locale}
              mainNavigation={mainNavigation}
              pricingNavLink={pricingNavLink}
              navOverrides={navOverrides}
            />
          ) : null}
        </div>
      </div>
    </header>
  );
}
